'use client'

import { useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import Link from 'next/link'

import Popover, { type PopoverProps } from 'shared/ui/Popover/Popover'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import styles from './Breadcrumbs.module.scss'

type Breadcrumb = {
  label: string
  path: string | null
}

type BreadcrumbsProps = {
  breadcrumbs: Breadcrumb[]
}

const StyledPopover = ({ className, ...props }: PopoverProps) => (
  <Popover className={clsx(styles.popover, className)} {...props} />
)

const Breadcrumbs = ({ breadcrumbs }: BreadcrumbsProps) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleOpen = () => {
    setIsOpen(true)
  }

  const mobileBreadcrumbs = useMemo(() => {
    if (isNotNilOrEmpty(breadcrumbs)) {
      const [first, ...rest] = breadcrumbs
      const last = rest.pop() || null
      const almostLast = rest.pop() || null

      return { first, last, almostLast, rest }
    } else {
      return { first: null, last: null, rest: null, almostLast: null }
    }
  }, [breadcrumbs])

  const isMoreThanOneBreadcrumb = useMemo(() => {
    return breadcrumbs.length > 1
  }, [breadcrumbs])

  return (
    <div className={styles.offLayout}>
      <nav className={styles.container} {...addTestId('breadcrumbs')}>
        <div className={styles.desktop}>
          {breadcrumbs?.map((breadcrumb: Breadcrumb, index: number) => (
            <div className={styles.breadcrumbWrapper} key={index}>
              {index !== 0 && <div className={styles.arrowRight} />}
              {breadcrumb.path ? (
                <Link href={breadcrumb.path}>
                  <span className={styles.breadcrumbItem}>
                    {breadcrumb.label}
                  </span>
                </Link>
              ) : (
                <span className={styles.breadcrumbItem}>
                  {breadcrumb.label}
                </span>
              )}
            </div>
          ))}
        </div>
        <div className={styles.mobile}>
          <div
            className={styles.mobileWrapper}
            key={mobileBreadcrumbs.first?.path}
          >
            {isMoreThanOneBreadcrumb ? (
              <Link href={mobileBreadcrumbs.first?.path || ''}>
                <span className={styles.breadcrumbItem}>
                  {mobileBreadcrumbs.first?.label}
                </span>
              </Link>
            ) : (
              <span className={styles.breadcrumbItem}>
                {mobileBreadcrumbs.first?.label}
              </span>
            )}

            {isNotNilOrEmpty(mobileBreadcrumbs.rest) && (
              <>
                <div className={styles.arrowRight} />
                <button
                  className={styles.menuOpenTrigger}
                  ref={anchorRef}
                  onClick={handleOpen}
                >
                  ...
                </button>
                <button
                  className={styles.menuOpenTrigger}
                  ref={anchorRef}
                  onClick={handleOpen}
                >
                  ...
                </button>
                <StyledPopover
                  id='breadcrumbs-mobile-menu'
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  anchorEl={anchorRef}
                  open={isOpen}
                  onClose={handleClose}
                >
                  <div className={styles.menuList}>
                    {mobileBreadcrumbs.rest?.map((b, idx) => {
                      return b.path ? (
                        <Link href={b.path} onClick={handleClose} key={idx}>
                          <div className={styles.menuItem}>{b.label}</div>
                        </Link>
                      ) : (
                        <div className={styles.menuItem} key={idx}>
                          {b.label}
                        </div>
                      )
                    })}
                  </div>
                </StyledPopover>
              </>
            )}
            {mobileBreadcrumbs.almostLast && (
              <>
                <div className={styles.arrowRight} />
                {mobileBreadcrumbs.almostLast?.path ? (
                  <Link href={mobileBreadcrumbs?.almostLast?.path}>
                    <span className={styles.breadcrumbItem}>
                      {mobileBreadcrumbs.almostLast?.label}
                    </span>
                  </Link>
                ) : (
                  <span className={styles.breadcrumbItem}>
                    {mobileBreadcrumbs.almostLast?.label}
                  </span>
                )}
              </>
            )}
            {mobileBreadcrumbs.last && (
              <>
                <div className={styles.arrowRight} />
                {mobileBreadcrumbs.last?.path ? (
                  <Link href={mobileBreadcrumbs.last.path}>
                    <span className={styles.breadcrumbItem}>
                      {mobileBreadcrumbs.last?.label}
                    </span>
                  </Link>
                ) : (
                  <span className={styles.breadcrumbItem}>
                    {mobileBreadcrumbs.last?.label}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </nav>
    </div>
  )
}

export default Breadcrumbs
